import { all, getContext, put, select, takeLatest } from 'redux-saga/effects';
import {
  fetchMessageNext,
  fetchMessagePrev,
  requestFocusOnMessage,
  scrollToRequestedMessage,
  scrollToRequestedMessageCleanup,
  scrollToRequestedMessageCleanupSuccess,
  waitForRenderBeforeScrollingToMessage,
} from '../../actions/room';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@wix/da-papi-types/lib/supabase';
import {
  fetchStreamContentsSupabase,
  takeStreamFetch,
} from '@wix/da-shared-react/pkg/Stream/helpers';
import ddt from '@wix/da-ddt';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import i18next from 'i18next';
import {
  getMessageCursor,
  getRoomStreamId,
  messagesFetchToMessageItem,
  splitMessageCursor,
} from '../../helpers';
import { getActiveRoomId, getFocusMessageRequest } from '../../selectors/room';
import { selectors, withCursor } from '@wix/da-shared-react/pkg/Stream';
import { getStream } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { MessageItem } from '../../../types';

import { resetStream } from '@wix/da-shared-react/pkg/Stream/withCursor/actions';
import { FetchType } from '@wix/da-shared-react/pkg/Stream/base/types';
import { updateRoomUnreadState } from '../../actions/roomUnread';
import { updateRoomStateForOpenedAt, updateRoomStateForScrollUp } from '../../helpers/roomState';
import { getRoomUnreadState } from '../../selectors/roomUnread';
import { FETCH_LIMIT_MESSAGES_AROUND } from '../../../constants';
import { realtimeConnections } from '../../RealtimeConnections';

enum FetchNextType {
  Head = 'head',
  OpenedAt = 'openedAt',
  Default = 'default',
}

function messagesAroundFetchToStreamData(
  messageId: string,
  results: Database['damz']['Functions']['get_messages_around']['Returns']
) {
  if (!Array.isArray(results) || results.length === 0) {
    return {
      hasMore: false,
      items: [],
    };
  }

  const indexOfRequestedMessage = results.findIndex(item => item.message_id === messageId);

  const hasLess = results.length - 1 - indexOfRequestedMessage === FETCH_LIMIT_MESSAGES_AROUND;
  const hasMore = indexOfRequestedMessage === FETCH_LIMIT_MESSAGES_AROUND;

  const newestItem = results[0];
  const oldestItem = results[results.length - 1];

  let fetchedItems: MessageItem[] = [];
  if (hasLess && hasMore) {
    fetchedItems = messagesFetchToMessageItem(results.slice(1, results.length - 1));
  } else if (hasLess) {
    fetchedItems = messagesFetchToMessageItem(results.slice(0, results.length - 1));
  } else if (hasMore) {
    fetchedItems = messagesFetchToMessageItem(results.slice(1, results.length));
  } else {
    // No overfetch to drop
    fetchedItems = messagesFetchToMessageItem(results);
  }

  return {
    hasLess,
    hasMore,
    items: fetchedItems.reverse(),
    prevCursor: hasLess ? getMessageCursor(oldestItem?.message_id, oldestItem?.sent_at) : undefined,
    cursor: hasMore ? getMessageCursor(newestItem?.message_id, newestItem?.sent_at) : undefined,
  };
}

function* handleMessageFetchNext({ payload: { streamId } }: ReturnType<typeof fetchMessageNext>) {
  const client: SupabaseClient<Database, 'damz'> = yield getContext('supaClient');

  const { cursor, limit, room_id, hasLess, message_id } = yield select(state => ({
    cursor: withCursor.selectors.getCursor(state, streamId),
    limit: selectors.getItemsPerFetch(state, streamId),
    hasLess: selectors.getHasLess(state, streamId),
    room_id: selectors.getStreamParam(state, streamId, 'room_id'),
    message_id: selectors.getStreamParam(state, streamId, 'message_id'),
  }));

  let fetchNextType = FetchNextType.Default;

  yield fetchStreamContentsSupabase<Database['damz']['Functions']['get_messages']['Returns']>({
    streamId,
    fetch: async () => {
      if (cursor) {
        const { messageId, sentAt } = splitMessageCursor(cursor);

        const response = await client.rpc('get_messages', {
          p_room_id: room_id,
          p_direction: 'next',
          p_cursor_sent_at: sentAt,
          p_cursor_msg_id: messageId,
          p_limit: limit,
        });

        return response;
      } else if (message_id) {
        fetchNextType = FetchNextType.OpenedAt;
        const response = await client.rpc('get_messages_around', {
          p_room_id: room_id,
          p_message_id: message_id,
          p_limit: FETCH_LIMIT_MESSAGES_AROUND,
        });
        return response;
      } else {
        fetchNextType = FetchNextType.Head;
        const response = await client.rpc('get_messages', {
          p_room_id: room_id,
          p_direction: 'head',
          p_limit: limit,
        });

        return response;
      }
    },
    getStreamData: function* (result) {
      const numResults = result.length;

      // Something probably wrong or the room is really empty
      if (numResults === 0 || !Array.isArray(result)) {
        return {
          hasMore: false,
          items: [],
        };
      }

      // When we do initial fetch we know there are no more new rows (give or take)
      if (fetchNextType === FetchNextType.Head) {
        const lastItem = result[numResults - 1];
        const prevCursor = getMessageCursor(lastItem?.message_id, lastItem?.sent_at);
        const initialHasLess = numResults > limit;

        if (numResults > limit) {
          result.pop();
        }

        return {
          hasMore: false,
          hasLess: initialHasLess,
          items: messagesFetchToMessageItem(result).reverse(),
          prevCursor,
        };
      } else if (fetchNextType === FetchNextType.OpenedAt) {
        return messagesAroundFetchToStreamData(message_id, result);
      } else {
        // We are fetching using a cursor going forwards
        const newestItem = result[numResults - 1];
        const hasMore = numResults > limit;
        const messages = messagesFetchToMessageItem(result);
        return {
          hasLess,
          hasMore,
          // Trim the message off the front since it'll be returned in the next fetch. SQL over fetches for the cursor.
          items: hasMore ? messages.slice(0, messages.length - 1) : messages,
          cursor: getMessageCursor(newestItem?.message_id, newestItem?.sent_at),
        };
      }
    },
    onSuccess: function* (results) {
      if (fetchNextType === FetchNextType.OpenedAt && message_id) {
        yield put(requestFocusOnMessage(message_id, true));
      }
    },
    fakeResponse: getFake(room_id, true, false, limit),
    onFailure: function* (error) {
      ddt.alert('damz', '[damz] Failed to fetch next messages', ['damz', 'db'], error);
      realtimeConnections.setMessageFetchFailed();
    },
  });
}

function* handleMessageFetchPrev({ payload: { streamId } }: ReturnType<typeof fetchMessagePrev>) {
  const client: SupabaseClient<Database, 'damz'> = yield getContext('supaClient');

  const { cursor, limit, room_id, hasMore } = yield select(state => ({
    hasMore: selectors.hasMore(state, streamId),
    cursor: withCursor.selectors.getPrevCursor(state, streamId),
    limit: selectors.getItemsPerFetch(state, streamId),
    room_id: selectors.getStreamParam(state, streamId, 'room_id'),
  }));

  yield fetchStreamContentsSupabase<Database['damz']['Functions']['get_messages']['Returns']>({
    streamId,
    fetchType: FetchType.Previous,
    fetch: async () => {
      if (cursor) {
        const { messageId: cursorMessageId, sentAt } = splitMessageCursor(cursor);

        const response = await client.rpc('get_messages', {
          p_room_id: room_id,
          p_direction: 'prev',
          p_cursor_sent_at: sentAt,
          p_cursor_msg_id: cursorMessageId,
          p_limit: limit,
        });

        return response;
      }
    },
    getStreamData: result => {
      const numResults = result.length;
      const oldestItem = result[numResults - 1];
      const hasLess = numResults > limit;
      const messages = messagesFetchToMessageItem(result);

      return {
        hasLess,
        hasMore,
        // Trim the message off the back since it'll be returned in the next fetch. SQL over fetches for the cursor.
        items: (hasLess ? messages.slice(0, messages.length - 1) : messages).reverse(),
        prevCursor: hasLess
          ? getMessageCursor(oldestItem?.message_id, oldestItem?.sent_at)
          : undefined,
      };
    },
    fakeResponse: getFake(room_id, false, true, limit),
    onFailure: function* (error) {
      ddt.alert('damz', '[damz] Failed to fetch less messages', ['damz', 'db'], error);
      yield put(addErrorNotification(i18next.t('damz:damz.room.messages.fetch.error')));
    },
  });
}

function* handleRequestFocusOnMessage(action: ReturnType<typeof requestFocusOnMessage>) {
  const { messageId, isInitialFetch } = action.payload;

  const activeRoomId: ReturnType<typeof getActiveRoomId> = yield select(getActiveRoomId);

  if (!activeRoomId) {
    return;
  }

  const { items, streamId }: ReturnType<typeof getStream<MessageItem>> = yield select(
    getStream,
    getRoomStreamId(activeRoomId)
  );

  const message = items.find(item => item.msg.message_id === messageId);

  // If the item is already present in the stream, we can scroll to it without additional network requests.
  // But we need to be sure that .
  if (message) {
    yield put(scrollToRequestedMessage(isInitialFetch));
    return;
  }

  const client: SupabaseClient<Database, 'damz'> = yield getContext('supaClient');

  yield fetchStreamContentsSupabase<
    Database['damz']['Functions']['get_messages_around']['Returns']
  >({
    streamId,
    fetch: async () => {
      const response = await client.rpc('get_messages_around', {
        p_room_id: activeRoomId,
        p_message_id: messageId,
        p_limit: FETCH_LIMIT_MESSAGES_AROUND,
      });

      return response;
    },
    getStreamData: function* (results) {
      const streamData = messagesAroundFetchToStreamData(messageId, results);

      yield put(resetStream(streamId));

      return streamData;
    },
    onSuccess: function* (results) {
      yield put(waitForRenderBeforeScrollingToMessage());
    },
    fakeResponse: getFake(activeRoomId, false, false, FETCH_LIMIT_MESSAGES_AROUND),
    onFailure: function* (error) {
      ddt.alert('damz', '[damz] Failed to fetch less messages', ['damz', 'db'], error);
      yield put(addErrorNotification(i18next.t('damz:damz.room.messages.fetch.error')));
    },
  });
}

function* handleScrollToRequestedMessageCleanup(
  action: ReturnType<typeof scrollToRequestedMessageCleanup>
) {
  const activeRoomId: ReturnType<typeof getActiveRoomId> = yield select(getActiveRoomId);

  const roomState: ReturnType<typeof getRoomUnreadState> = yield select(
    getRoomUnreadState,
    activeRoomId
  );

  const focusMessageRequest: ReturnType<typeof getFocusMessageRequest> =
    yield select(getFocusMessageRequest);

  if (!activeRoomId || !roomState) {
    return;
  }

  if (focusMessageRequest.requiredFetch) {
    yield put(updateRoomUnreadState(activeRoomId, updateRoomStateForOpenedAt(roomState)));
  } else {
    yield put(updateRoomUnreadState(activeRoomId, updateRoomStateForScrollUp(roomState)));
  }

  yield put(scrollToRequestedMessageCleanupSuccess());
}

/**
 * Message fetching saga - handles message pagination and scrolling
 */
export function* roomMessageFetchingSaga() {
  yield all([
    takeStreamFetch(fetchMessageNext, handleMessageFetchNext),
    takeStreamFetch(fetchMessagePrev, handleMessageFetchPrev),
    takeLatest(requestFocusOnMessage, handleRequestFocusOnMessage),
    takeLatest(scrollToRequestedMessageCleanup, handleScrollToRequestedMessageCleanup),
  ]);
}

// Testing stuff below
function getFake(
  room_id: string,
  hasMore: boolean,
  hasLess: boolean,
  limit: number,
  seededMessageId?: string
) {
  return options => {
    if (process.env.NODE_ENV !== 'production') {
      if ((window as any).doError) {
        return {
          error: 'invalid_request',
          errorDescription: 'Not logged in.',
          status: 'error',
        };
      }
      // eslint-disable-next-line
      const Chance = require('chance').Chance;
      const chance = new Chance();
      // eslint-disable-next-line
      const users = require('@wix/da-sample-data').users;

      const seededMessageIdIndex = 15;
      const possibleAuthors: any[] = chance.pickset(users, 3);
      const messages: Database['damz']['Functions']['get_messages']['Returns'] = Array.from(
        { length: limit },
        (_, index): Database['damz']['Functions']['get_messages']['Returns'][0] => {
          const author: any = chance.pickone(possibleAuthors);
          return {
            message_id: seededMessageIdIndex === index ? seededMessageId : chance.guid(),
            room_id: room_id,
            user_id: author.useridUuid,
            msg_type: 0,
            format: 0,
            msg: chance.paragraph(),
            sent_at: `${chance.timestamp()}`,
            edit_at: '',
            deleted_at: '',
            msg_username: author.username,
            msg_usericon: 0,
            msg_symbol: '',
            msg_user_id: '',
            msg_user_id_int: 0,
            msg_user_banned: false,
            reactions: {},
            a_attachment: '',
            b_attachment: '',
            c_attachment: '',
            d_attachment: '',
            e_attachment: '',
            f_attachment: '',
          };
        }
      );

      return { data: messages };
    }
  };
}
